import { getVideoEmbedUrl } from "@/lib/videoEmbed";

import { PillLink } from "./PillLink";
import { routes, sectionShell } from "./landingContent";

export interface ContentBlock {
  id: string;
  block_type:
    | "image"
    | "video"
    | "text"
    | "heading"
    | "featured_programs"
    | "how_it_works"
    | "for_entrepreneurs_mentors"
    | "cta";
  title: string | null;
  content: string | null;
  media_url: string | null;
  sequence_order: number;
  is_active: boolean;
  is_system?: boolean;
}

/**
 * Admin-authored content blocks (heading / text / image / video), rendered in
 * the Signal design language.
 *
 * The design covers the fixed sections only, so these follow its vocabulary —
 * white cards at radius 26 on the off-white surface — instead of the old
 * alternating muted/secondary section bands, which had no equivalent here.
 */
export default function CustomBlock({ block }: { block: ContentBlock }) {
  if (block.block_type === "heading") {
    return (
      <section className={`${sectionShell} pt-[76px]`}>
        <h2 className="text-pretty text-[36px] font-extrabold tracking-[-0.03em] text-eab-neutral-900 lg:text-[54px]">
          {block.title}
        </h2>
      </section>
    );
  }

  if (block.block_type === "text") {
    // "Start today" keeps its emphasis treatment: a blue panel with a sign-up
    // pill, matching the closing CTA rather than the old gradient band.
    const isStartToday = block.title?.toLowerCase().includes("start today");

    if (isStartToday) {
      return (
        <div className="mx-auto w-full max-w-[1440px] px-4 pt-[76px]">
          <div className="rounded-[28px] bg-eab-blue-dark px-6 py-12 text-white sm:px-10 lg:px-14 lg:py-[68px]">
            {block.title && (
              <h3 className="mb-5 max-w-[760px] text-pretty text-[32px] font-extrabold leading-[1.1] tracking-[-0.03em] lg:text-[44px]">
                {block.title}
              </h3>
            )}
            <p className="max-w-[760px] whitespace-pre-wrap text-pretty text-[19px] leading-[1.55] text-white/[0.88]">
              {block.content}
            </p>
            <div className="mt-8">
              <PillLink to={routes.signUp} variant="green">
                Join now →
              </PillLink>
            </div>
          </div>
        </div>
      );
    }

    return (
      <section className={`${sectionShell} pt-[76px]`}>
        <div className="rounded-[26px] bg-white p-8 lg:p-12">
          {block.title && (
            <h3 className="mb-4 text-pretty text-[28px] font-bold leading-[1.15] tracking-[-0.02em] text-eab-neutral-900 lg:text-[34px]">
              {block.title}
            </h3>
          )}
          <p className="max-w-[760px] whitespace-pre-wrap text-pretty text-[17px] leading-[1.6] text-eab-neutral-700">
            {block.content}
          </p>
        </div>
      </section>
    );
  }

  if (block.block_type === "image" && block.media_url) {
    return (
      <section className={`${sectionShell} pt-[76px]`}>
        {/* Capped rather than full-bleed: a 1312px-wide 16:9 media block would
            tower over the fixed sections around it. */}
        <figure className="mx-auto max-w-[980px]">
          <img
            src={block.media_url}
            alt={block.title || "Homepage image"}
            className="max-h-[520px] w-full rounded-[26px] object-cover"
            loading="lazy"
          />
          {block.title && (
            <figcaption className="mt-3 text-[15px] text-eab-neutral-500">
              {block.title}
            </figcaption>
          )}
        </figure>
      </section>
    );
  }

  if (block.block_type === "video" && block.media_url) {
    return (
      <section className={`${sectionShell} pt-[76px]`}>
        <figure className="mx-auto max-w-[980px]">
          <div className="aspect-video w-full overflow-hidden rounded-[26px]">
            <iframe
              src={getVideoEmbedUrl(block.media_url)}
              title={block.title || "Homepage video"}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {block.title && (
            <figcaption className="mt-3 text-[15px] text-eab-neutral-500">
              {block.title}
            </figcaption>
          )}
        </figure>
      </section>
    );
  }

  return null;
}
